<template>
  <div class="uploadWrap">
    <img v-show="previewImg" id="previewImg" width="100%" :src="previewImg" />
    <label for="upload-photo" class="uploadArea">
      <!-- <div class="uploadBtn">เลือกไฟล์...</div> -->
      <div v-if="!uploading">เลือกไฟล์...</div>
      <div v-if="uploading">กำลังโหลด...</div>
    </label>
    <input
      type="file"
      name="photo"
      ref="fileInput"
      :capture="getMobileOperatingSystem() == 'iOS' ? false : true"
      accept="image/*"
      @change="fileChange"
      id="upload-photo"
    />
    <div v-if="!noFile" class="remove mt-1" @click="reset()">
      ลบไฟล์ที่เลือก
    </div>
  </div>
</template>
<script>
import Compressor from "compressorjs";

// ไฟล์ต้นฉบับใหญ่สุดที่รับมาย่อ (ต้อง decode ทั้งรูปในมือถือ ใหญ่กว่านี้เสี่ยง LINE browser ค้าง)
const MAX_INPUT_SIZE = 30 * 1024 * 1024;
// ไฟล์ที่ส่งขึ้น server ต้องไม่เกิน 8MB
const MAX_UPLOAD_SIZE = 8 * 1024 * 1024;
// ย่อรอบแรกตามเดิม ถ้ายังเกิน MAX_UPLOAD_SIZE ค่อยลดขนาด/คุณภาพลงทีละขั้น
const COMPRESS_STEPS = [
  { quality: 0.6, maxSize: 2400 },
  { quality: 0.5, maxSize: 2000 },
  { quality: 0.4, maxSize: 1600 },
];
// brand ใน ftyp ที่บอกว่าเป็น HEIF ตามมาตรฐาน ISO/IEC 23008-12
const HEIF_BRANDS = [
  "heic",
  "heix",
  "hevc",
  "hevx",
  "heim",
  "heis",
  "heif",
  "mif1",
  "mif2",
  "mif3",
  "msf1",
  "miaf",
];
const AVIF_BRANDS = ["avif", "avis"];

export default {
  props: { imageUrl: String },
  data() {
    return {
      previewImg: this.imageUrl || null,
      uploading: false, // loading
      noFile: true,
    };
  },
  watch: {
    imageUrl(val) {
      this.previewImg = val;
    },
  },
  methods: {
    reset() {
      this.previewImg = null;
      this.uploading = false;
      this.$refs.fileInput.value = null;
      this.noFile = true;
      this.$emit("result", {});
      this.previewImg = this.imageUrl;
    },
    async fileChange(e) {
      const file = e.target.files[0];
      this.noFile = !file;

      if (!file) {
        return;
      }

      this.uploading = true;

      if (file.size > MAX_INPUT_SIZE) {
        this.Swal.fire("", "ขนาดไฟล์ไม่ควรเกิน 30MB", "warning");
        this.uploading = false;
        return;
      }

      // ดูชนิดไฟล์จาก byte ต้นไฟล์ เพราะชื่อ/type ที่ได้จากมือถือเชื่อไม่ได้
      // (เช่น JPEG ที่ชื่อ .heif หรือ type เป็นค่าว่าง)
      let result;
      try {
        const type = await this.detectImageType(file);
        if (type === "image/heic") {
          result = await this.compressHeic(file);
        } else if (type && type !== file.type) {
          result = await this.compressImage(new Blob([file], { type }));
        } else {
          result = await this.compressImage(file);
        }
      } catch (err) {
        this.onFileError(file, err);
        return;
      }

      if (result.size > MAX_UPLOAD_SIZE) {
        this.Swal.fire("", "ขนาดไฟล์ไม่ควรเกิน 8MB", "warning");
        this.uploading = false;
        return;
      }

      this.preview(result);
      this.uploading = false;
      this.$emit("result", {
        file: result,
        fileName: this.toJpegFileName(file.name),
      });
    },
    async compressImage(image) {
      let result;
      for (const step of COMPRESS_STEPS) {
        result = await this.resizeImage(image, step);
        if (result.size <= MAX_UPLOAD_SIZE) break;
      }
      return result;
    },
    async compressHeic(file) {
      // ถ้า browser decode HEIC เองได้ (iOS 17+) ให้ใช้ทางนี้ ซึ่งเร็วกว่ามาก
      // เครื่องที่ decode เองไม่ได้ หรือย่อไม่สำเร็จ จึงค่อยโหลด heic-to มาแปลง
      const heic = new Blob([file], { type: "image/heic" });
      if (await this.canDecode(heic)) {
        try {
          const result = await this.compressImage(heic);
          // Compressor คืนไฟล์ต้นฉบับเมื่อ canvas แปลงไม่ได้ ซึ่งยังเป็น HEIC อยู่
          if (result.type === "image/jpeg") return result;
        } catch (err) {
          console.log(err.message);
        }
      }
      return this.compressImage(await this.convertHeicToJpeg(file));
    },
    readHeader(file, length) {
      return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(new Uint8Array(fr.result));
        fr.onerror = () => reject(fr.error);
        fr.readAsArrayBuffer(file.slice(0, length));
      });
    },
    async detectImageType(file) {
      const b = await this.readHeader(file, 64);
      const ascii = (start, end) =>
        String.fromCharCode.apply(null, b.subarray(start, end));

      if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
      if (b[0] === 0x89 && ascii(1, 4) === "PNG") return "image/png";
      if (ascii(0, 4) === "GIF8") return "image/gif";
      if (ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP") return "image/webp";
      if (ascii(4, 8) === "ftyp") {
        const major = ascii(8, 12);
        // AVIF ก็ระบุ mif1 ใน compatible brands ต้องแยกออกก่อน
        if (AVIF_BRANDS.includes(major)) return "image/avif";

        const end = Math.min(new DataView(b.buffer).getUint32(0), b.length);
        const brands = [major];
        for (let i = 16; i + 4 <= end; i += 4) brands.push(ascii(i, i + 4));
        if (brands.some((brand) => HEIF_BRANDS.includes(brand))) {
          return "image/heic";
        }
      }
      return file.type;
    },
    canDecode(blob) {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        const done = (ok) => {
          URL.revokeObjectURL(url);
          resolve(ok);
        };
        img.onload = () => done(true);
        img.onerror = () => done(false);
        img.src = url;
      });
    },
    async convertHeicToJpeg(file) {
      const { heicTo } = await import(
        /* webpackChunkName: "heic-to" */ "heic-to"
      );
      const bitmap = await heicTo({ blob: file, type: "bitmap" });

      // วาดลง canvas ขนาดที่ย่อแล้วเท่านั้น เพราะ iOS จำกัดพื้นที่ canvas
      // ราว 16.7 ล้านพิกเซล รูป 24-48MP เต็มขนาดจะขอ context ไม่ได้
      const scale = Math.min(
        1,
        COMPRESS_STEPS[0].maxSize / Math.max(bitmap.width, bitmap.height)
      );
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas
        .getContext("2d")
        .drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();

      return new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.8)
      );
    },
    toJpegFileName(name) {
      const base = (name || "image").replace(/\.[^.]+$/, "");
      return `${base}.jpg`;
    },
    onFileError(file, err) {
      // heic-to reject เป็น string ไม่ใช่ Error
      const message = err?.message || String(err);
      console.log(message);
      window.$pushEvent?.(
        "upload_card_image",
        "error",
        `${file.type || "unknown"}|${file.size}|${message}`
      );
      this.uploading = false;
      // ใส่สาเหตุไว้ในข้อความ ให้ลูกค้าแคปหน้าจอส่งเจ้าหน้าที่ได้
      this.Swal.fire(
        "",
        `กรุณาตรวจสอบไฟล์ที่เลือกอีกครั้ง หรือติดต่อเจ้าหน้าที่ (${message.slice(0, 80)})`,
        "warning"
      );
    },
    preview(file) {
      const fr = new FileReader();
      fr.readAsDataURL(file);
      fr.onload = (e) => {
        this.previewImg = e.target.result;
      };
    },
    resizeImage(file, { quality, maxSize }) {
      return new Promise((resolve, reject) => {
        new Compressor(file, {
          quality,
          convertSize: 0,
          maxWidth: maxSize,
          maxHeight: maxSize,
          mimeType: "image/jpeg",
          success: resolve,
          error: reject,
        });
      });
    },
    getMobileOperatingSystem() {
      var userAgent = navigator.userAgent || navigator.vendor || window.opera;

      // Windows Phone must come first because its UA also contains "Android"
      if (/windows phone/i.test(userAgent)) {
        return "Windows Phone";
      }

      if (/android/i.test(userAgent)) {
        return "Android";
      }

      // iOS detection from: http://stackoverflow.com/a/9039885/177710
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return "iOS";
      }

      return "unknown";
    },
  },
  mounted() {
    window.loading(false);
  },
};
</script>
<style lang="scss">
.uploadWrap {
  user-select: none;
  max-width: 400px;
  margin: 0 auto;
  label {
    cursor: pointer;
    /* Style as you please, it will become the visible UI component. */
  }

  #upload-photo {
    opacity: 0;
    position: absolute;
    display: none;
    z-index: -1;
  }

  .uploadBtn {
    background: cornflowerblue;
    border-radius: 6px;
    color: #ffffff;
    padding: 1px 5px;
    &:active {
      box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
    }
  }

  .uploadArea {
    width: 100%;
    border: 2px dashed rgb(0 0 0 / 25%);
    height: 35px;
    margin: 5px auto;
    text-align: center;
    color: rgb(0 0 0 / 55%);
  }
  .remove {
    width: 100%;
    font-size: 14px;
    background: rgb(197, 39, 39);
    border-radius: 6px;
    cursor: pointer;
    color: #ffffff;
    text-align: center;
  }
}
</style>